import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShoppingCart, Plus, Trash2, ScanLine, Receipt, Printer, X, Search } from "lucide-react";

export default function AdminSales() {
  const [parts, setParts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [notifySms, setNotifySms] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [partSearch, setPartSearch] = useState("");
  const [recentSales, setRecentSales] = useState([]);
  const [completed, setCompleted] = useState(null);
  const scanRef = useRef(null);

  const reload = () => {
    api.get("/admin/parts").then((r) => setParts(r.data));
    api.get("/admin/sales?limit=10").then((r) => setRecentSales(r.data));
  };
  useEffect(() => { reload(); }, []);

  const addToCart = (part, qty = 1) => {
    if ((part.stock || 0) < qty) { toast.error(`Stoc insuficient pentru ${part.name}`); return; }
    setCart((c) => {
      const idx = c.findIndex((x) => x.part_id === part.id);
      if (idx >= 0) {
        const next = [...c];
        if ((part.stock || 0) < next[idx].quantity + qty) { toast.error("Stoc insuficient"); return c; }
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...c, { part_id: part.id, name: part.name, sku: part.sku, unit_price: part.price, quantity: qty }];
    });
  };

  const removeFromCart = (idx) => setCart((c) => c.filter((_, i) => i !== idx));
  const updateQty = (idx, qty) => setCart((c) => c.map((x, i) => i === idx ? { ...x, quantity: Math.max(1, parseInt(qty) || 1) } : x));

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;
    try {
      const r = await api.get(`/admin/parts/by-sku/${encodeURIComponent(scanInput.trim())}`);
      addToCart(r.data);
      toast.success(`+ ${r.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cod inexistent");
    }
    setScanInput("");
    scanRef.current?.focus();
  };

  const subtotal = cart.reduce((s, l) => s + (l.unit_price * l.quantity), 0);
  const total = Math.max(0, subtotal - (parseFloat(discount) || 0));

  const finalize = async () => {
    if (cart.length === 0) { toast.error("Cosul este gol"); return; }
    try {
      const r = await api.post("/admin/sales", {
        customer_name: customerName,
        customer_phone: customerPhone,
        items: cart.map((c) => ({ part_id: c.part_id, quantity: c.quantity })),
        discount: parseFloat(discount) || 0,
        payment_method: paymentMethod,
        notes,
        notify_sms: notifySms && !!customerPhone,
      });
      toast.success(`Vanzare ${r.data.receipt_no} finalizata!`);
      setCompleted(r.data);
      setCart([]); setCustomerName(""); setCustomerPhone(""); setDiscount(0); setNotes(""); setNotifySms(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare"); }
  };

  const filteredParts = parts.filter((p) => {
    if (!partSearch.trim()) return (p.stock || 0) > 0;
    const q = partSearch.toLowerCase();
    return ((p.stock || 0) > 0) && (p.name + " " + (p.sku || "") + " " + (p.category || "")).toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-sales-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3"><ShoppingCart className="w-8 h-8 text-[#00E5FF]" /> <h1 className="font-display text-4xl uppercase">Vanzare directa</h1></div>
          <p className="text-sm text-neutral-400 mt-1">POS rapid pentru clientii care cumpara doar piese (fara reparatie)</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT: Parts selection */}
        <div className="lg:col-span-2 space-y-4">
          <form onSubmit={handleScan} className="bg-neutral-950 border border-[#00E5FF] p-3 flex gap-2">
            <ScanLine className="w-5 h-5 text-[#00E5FF] self-center" />
            <Input ref={scanRef} autoFocus value={scanInput} onChange={(e) => setScanInput(e.target.value)} placeholder="Scaneaza cu Zebra sau introdu codul (Enter)" className="bg-neutral-900 border-neutral-800 rounded-none font-mono h-11" data-testid="sales-scan-input" />
            <Button type="submit" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none h-11"><Plus className="w-4 h-4" /></Button>
          </form>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input value={partSearch} onChange={(e) => setPartSearch(e.target.value)} placeholder="Cauta piesa..." className="bg-neutral-900 border-neutral-800 rounded-none pl-10 h-11" />
          </div>

          <div className="bg-neutral-950 border border-neutral-800 max-h-[480px] overflow-y-auto">
            {filteredParts.length === 0 ? (
              <div className="p-8 text-center text-sm text-neutral-500">{parts.length === 0 ? "Nu ai piese in stoc" : "Niciun rezultat"}</div>
            ) : (
              <div className="divide-y divide-neutral-900">
                {filteredParts.map((p) => (
                  <button key={p.id} onClick={() => addToCart(p)} className="w-full p-3 text-left flex items-center gap-3 hover:bg-neutral-900 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-[10px] text-neutral-500 font-mono">{p.sku}</div>
                    </div>
                    <div className="text-xs text-neutral-400">stoc: <span className="text-white">{p.stock}</span></div>
                    <div className="text-[#00E5FF] font-semibold w-20 text-right">{p.price} RON</div>
                    <Plus className="w-4 h-4 text-neutral-500" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Cart */}
        <div className="space-y-4">
          <div className="bg-neutral-950 border border-neutral-800 p-4">
            <h3 className="font-display text-lg uppercase mb-3 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-[#00E5FF]" /> Cos ({cart.length})
            </h3>
            {cart.length === 0 ? (
              <div className="text-xs text-neutral-500 py-6 text-center">Cosul e gol. Scaneaza sau alege o piesa.</div>
            ) : (
              <div className="space-y-2">
                {cart.map((l, i) => (
                  <div key={i} className="border border-neutral-800 p-2">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{l.name}</div>
                        <div className="text-[10px] text-neutral-500 font-mono">{l.sku}</div>
                      </div>
                      <button onClick={() => removeFromCart(i)} className="text-neutral-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input type="number" min="1" value={l.quantity} onChange={(e) => updateQty(i, e.target.value)} className="w-16 h-7 bg-neutral-900 border-neutral-800 rounded-none text-center text-sm" />
                      <span className="text-xs text-neutral-500">x {l.unit_price} =</span>
                      <span className="ml-auto text-[#00E5FF] font-semibold text-sm">{(l.quantity * l.unit_price).toFixed(0)} RON</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 space-y-2 border-t border-neutral-800 pt-3">
              <Row label="Subtotal" value={`${subtotal.toFixed(0)} RON`} />
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-neutral-400">Discount</span>
                <Input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-24 h-7 bg-neutral-900 border-neutral-800 rounded-none text-right text-sm" />
              </div>
              <div className="flex items-center justify-between border-t border-neutral-800 pt-2">
                <span className="font-display uppercase">Total</span>
                <span className="font-display text-3xl text-[#00E5FF]">{total.toFixed(0)} RON</span>
              </div>
            </div>
          </div>

          <div className="bg-neutral-950 border border-neutral-800 p-4 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Client (optional)</Label>
            <Input placeholder="Nume" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-none" />
            <Input placeholder="Telefon" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-none" />
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger className="bg-neutral-900 border-neutral-800 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                <SelectItem value="cash">Numerar</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="transfer">Transfer bancar</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Note" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-none" />
            {customerPhone && (
              <div className="flex items-center gap-2">
                <Checkbox id="notify_sms" checked={notifySms} onCheckedChange={(v) => setNotifySms(!!v)} className="border-neutral-700 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:border-[#00E5FF] rounded-none" />
                <Label htmlFor="notify_sms" className="text-xs cursor-pointer">Trimite SMS de multumire</Label>
              </div>
            )}
          </div>

          <Button onClick={finalize} disabled={cart.length === 0} data-testid="finalize-sale-btn" className="w-full bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none h-14 text-base">
            <Receipt className="w-5 h-5 mr-2" /> Finalizeaza vanzarea
          </Button>
        </div>
      </div>

      {/* Recent sales */}
      <div className="mt-12 bg-neutral-950 border border-neutral-800 p-5">
        <h3 className="font-display text-xl uppercase mb-4">Vanzari recente</h3>
        {recentSales.length === 0 ? (
          <div className="text-sm text-neutral-500 py-4 text-center">Nicio vanzare inca</div>
        ) : (
          <div className="space-y-1">
            {recentSales.map((s) => (
              <div key={s.id} className="flex items-center gap-3 text-sm border-b border-neutral-900 py-2">
                <span className="font-mono text-[10px] text-[#00E5FF] uppercase">{s.receipt_no}</span>
                <span className="text-neutral-400 text-xs">{new Date(s.created_at).toLocaleString("ro-RO")}</span>
                <span className="text-neutral-300">{s.items.length} piese</span>
                {s.customer_name && <span className="text-neutral-500 text-xs">· {s.customer_name}</span>}
                <span className="ml-auto text-[#00E5FF] font-semibold">{s.total.toFixed(0)} RON</span>
                <button onClick={() => window.open(`/admin/vanzari/${s.id}/print`, "_blank")} className="text-neutral-400 hover:text-[#00E5FF]">
                  <Printer className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Completed sale modal */}
      {completed && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className="bg-neutral-950 border-2 border-[#00E5FF] max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-green-400">Vanzare reusita</div>
                <div className="font-display text-2xl uppercase mt-1">Bon #{completed.receipt_no}</div>
              </div>
              <button onClick={() => setCompleted(null)} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-1 text-sm border-y border-neutral-800 py-3 mb-4">
              {completed.items.map((l, i) => (
                <div key={i} className="flex justify-between">
                  <span>{l.quantity}x {l.name}</span>
                  <span className="text-neutral-300">{l.line_total} RON</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-baseline mb-5">
              <span className="text-sm uppercase tracking-widest text-neutral-400">Total</span>
              <span className="font-display text-4xl text-[#00E5FF]">{completed.total} RON</span>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => window.open(`/admin/vanzari/${completed.id}/print`, "_blank")} className="flex-1 bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">
                <Printer className="w-4 h-4 mr-2" /> Printeaza bon
              </Button>
              <Button onClick={() => setCompleted(null)} variant="outline" className="rounded-none border-neutral-700">Inchide</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-neutral-400">{label}</span>
    <span className="text-white">{value}</span>
  </div>
);
